import React from 'react';
import { ModeComment } from '@lendi-ui/icon';

import { FREQUENCY, PII_FIELD_MASKING_CLASS } from '@funnels/constants';
import { PropertyAsset, PropertyExpenses, StoreApplicant } from '@funnels/types';
import { BoldHeading } from '@funnels/ui-components';
import { Table } from '@funnels/ui-components/Common';
import { ExpenseAmountFunnelReadOnlyView } from '@funnels/ui-components/Questions/ExpenseAmount';
import { ExpenseFrequencyReadOnlyView } from '@funnels/ui-components/Questions/ExpenseFrequency';
import { ExpenseTypeFunnelReadOnlyView } from '@funnels/ui-components/Questions/ExpenseType';
import {
  convert,
  getAnnualValueFromFrequency,
  getApplicantName,
  getFieldValueLabel,
  PropertyInGroup,
} from '@funnels/utils';

import { TableWrapper } from './style';

export type PropertyExpenseTableProps = {
  group: PropertyInGroup;
  title: string;
  applicants: StoreApplicant[];
  hasComment: boolean;
} & Pick<
  React.ComponentProps<typeof Table>,
  'commentsModalTitle' | 'commentsModalContent' | 'renderCommentsModalFooter'
>;

function PropertyExpenseTable({
  group,
  title,
  applicants,
  hasComment,
  commentsModalTitle,
  commentsModalContent,
  renderCommentsModalFooter,
}: PropertyExpenseTableProps) {
  const ownerHeaders = group.applicantIds.flatMap((applicantId) => [
    {
      content: `${getApplicantName(applicants, applicantId)}'s amount`,
      minWidth: 144,
    },
    {
      content: 'Frequency',
      minWidth: 144,
    },
  ]);

  const HEADERS = [
    { content: 'Category', minWidth: 484 },
    ...ownerHeaders,
    { content: 'Annual Total', minWidth: 144 },
    { content: 'Comments', minWidth: 144 },
  ];

  return (
    <TableWrapper>
      <Table
        title={title}
        headers={HEADERS}
        commentsButtonPosition="title"
        commentsModalTitle={commentsModalTitle}
        commentsModalContent={commentsModalContent}
        renderCommentsModalFooter={renderCommentsModalFooter}
      >
        {group.properties?.length > 0 &&
          group.properties.map((property: PropertyAsset, index: number) => (
            <React.Fragment key={property.id}>
              <tr>
                <td colSpan={HEADERS.length}>
                  <BoldHeading size="xs" className={PII_FIELD_MASKING_CLASS}>{`Property ${index + 1}: ${
                    property.address?.formatted || ''
                  }`}</BoldHeading>
                </td>
              </tr>
              {property.propertyExpenses?.map((expense: PropertyExpenses, expenseIndex: number) => {
                const expenseType = expense.type;
                const expenseTypeLabel = getFieldValueLabel(expenseType, expenseType);
                const expenseFrequency = (expense.frequency as FREQUENCY | undefined) || FREQUENCY.MONTHLY;
                const expenseAmount = convert(expense.amount || 0, expenseFrequency, FREQUENCY.MONTHLY);
                const expenseIsUndefined = expense.amount === undefined;
                const frequency = FREQUENCY.MONTHLY;

                const ownersExpenseAmount = group.applicantIds.map((applicantId) => {
                  const ownership =
                    property.percentOwned?.owners.find((owner) => owner.applicantId === applicantId)?.percent || 0;
                  return ownership * expenseAmount;
                });

                const annualTotal = getAnnualValueFromFrequency(
                  ownersExpenseAmount.reduce((sum, val) => sum + val, 0),
                  frequency
                );

                return (
                  <tr key={`expense-${index}-${expenseIndex}`}>
                    <td>
                      <ExpenseTypeFunnelReadOnlyView value={expenseTypeLabel} textSize="md" />
                    </td>
                    {ownersExpenseAmount.map((amount, expenseOwnerIndex) => (
                      <React.Fragment key={`expense-owner-${index}-${expenseIndex}-${expenseOwnerIndex}`}>
                        <td>
                          <ExpenseAmountFunnelReadOnlyView
                            value={expenseIsUndefined ? undefined : amount}
                            textSize="md"
                          />
                        </td>
                        <td>
                          <ExpenseFrequencyReadOnlyView value={frequency} textSize="md" />
                        </td>
                      </React.Fragment>
                    ))}
                    <td>
                      <ExpenseAmountFunnelReadOnlyView
                        value={expenseIsUndefined ? undefined : annualTotal}
                        textSize="md"
                      />
                    </td>
                    <td>{hasComment && <ModeComment />}</td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
      </Table>
    </TableWrapper>
  );
}

export default PropertyExpenseTable;
